import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { ThemeSwitcher } from '@/components/ui/theme-switcher';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bot, CheckSquare, Users, FileCheck, BarChart3, Puzzle, 
  Check, Star, ArrowRight, Play, Sparkles, Target, Shield, Zap
} from 'lucide-react';

const testimonials = [
  { name: 'Sarah Chen', role: 'COO, TechStart', quote: 'Reduced our operational overhead by 40%. The system handles routine tracking so we can focus on strategy.', avatar: 'SC' },
  { name: 'Ahmed Hassan', role: 'Operations Director', quote: 'Finally, a tool that understands how we work. The approval workflows alone saved us 10 hours per week.', avatar: 'AH' },
  { name: 'Priya Sharma', role: 'Team Lead', quote: 'My team loves it. Clear task tracking, smart workload alerts, and everything just works.', avatar: 'PS' },
];

export default function Landing() {
  const { t } = useTranslation();

  const features = [
    { 
      icon: Target, 
      title: 'Operations Control', 
      desc: 'Track tasks, approvals, and team workload in one unified dashboard. Execute decisions, not manage chaos.' 
    },
    { 
      icon: CheckSquare, 
      title: 'Smart Task Management', 
      desc: 'Kanban boards, time tracking, dependencies, and automated workflows that adapt to your team.' 
    },
    { 
      icon: Users, 
      title: 'Team Coordination', 
      desc: 'Real-time updates, workload balancing, and capacity planning. Know who\'s doing what, always.' 
    },
    { 
      icon: FileCheck, 
      title: 'Approval Workflows', 
      desc: 'Streamlined approval process for budgets, expenses, and requests. No more email chains.' 
    },
    { 
      icon: BarChart3, 
      title: 'Operational Insights', 
      desc: 'Track productivity, identify bottlenecks, and detect risks before they become problems.' 
    },
    { 
      icon: Bot, 
      title: 'Optional AI Assistant', 
      desc: 'AI-powered suggestions for task delegation and deadline risks. Works great without it too.' 
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-lg">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold">OpsGPT</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <Link to="/auth">
              <Button>{t('auth.signIn')}</Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="container mx-auto px-4 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Badge className="mb-4 gap-2">
            <Shield className="h-3 w-3" />
            Business Operations Control System
          </Badge>
          <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-6xl">
            Execute Decisions,<br />
            <span className="text-primary">Not Manage Chaos</span>
          </h1>
          <p className="mx-auto mb-4 max-w-2xl text-lg text-muted-foreground">
            OpsGPT helps teams track work, approvals, and risks — with optional AI intelligence.
          </p>
          <p className="mx-auto mb-8 max-w-xl text-sm text-muted-foreground font-medium">
            <strong className="text-foreground">This is NOT a CRM.</strong> OpsGPT handles execution after decisions are made.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="gap-2">Get Started Free <ArrowRight className="h-4 w-4" /></Button>
            </Link>
            <Button size="lg" variant="outline" className="gap-2"><Play className="h-4 w-4" /> Watch Demo</Button>
          </div>
        </motion.div>
      </section>

        {/* What OpsGPT Is / Isn't */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="sr-only">What OpsGPT is and isn't</h2>
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
          <Card className="border-success/30 bg-success/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-success">
                <Check className="h-5 w-5" />
                What OpsGPT IS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success" />Operations control dashboard</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success" />Task & approval workflow system</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success" />Team workload & capacity tracker</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success" />Risk & deadline monitoring</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-success" />Audit trail & activity logs</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="border-destructive/30 bg-destructive/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-400">
                <Zap className="h-5 w-5" />
                What OpsGPT is NOT
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">❌ Not a CRM (no leads, deals, pipelines)</li>
                <li className="flex items-center gap-2">❌ Not a chat app (use Slack for that)</li>
                <li className="flex items-center gap-2">❌ Not HR software (no payroll, attendance)</li>
                <li className="flex items-center gap-2">❌ Not project management (no Gantt charts)</li>
                <li className="flex items-center gap-2">❌ Not AI hype (practical ops focus)</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Everything you need to run operations</h2>
          <p className="text-muted-foreground">Focused tools for teams that ship</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardHeader>
                  <f.icon className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>{f.title}</CardTitle>
                </CardHeader>
                <CardContent><p className="text-muted-foreground">{f.desc}</p></CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust Signals */}
      <section className="container mx-auto px-4 py-12 bg-muted/30">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2">Enterprise Ready</h2>
          <p className="text-muted-foreground text-sm">Security and compliance built-in</p>
        </div>
        <div className="flex flex-wrap justify-center gap-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-success" />Row-Level Security</div>
          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-success" />Audit Logs</div>
          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-success" />Data Export (CSV/PDF)</div>
          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-success" />Role-Based Access</div>
          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-success" />Webhook Integrations</div>
        </div>
      </section>

      {/* Pricing */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">{t('landing.pricing.title')}</h2>
          <p className="text-muted-foreground">{t('landing.pricing.subtitle')}</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
          {['starter', 'professional', 'enterprise'].map((plan, i) => (
            <Card key={plan} className={i === 1 ? 'border-primary shadow-lg scale-105' : ''}>
              <CardHeader>
                {i === 1 && <Badge className="w-fit mb-2">Most Popular</Badge>}
                <CardTitle>{t(`landing.pricing.${plan}.name`)}</CardTitle>
                <div className="text-3xl font-bold">{t(`landing.pricing.${plan}.price`)}<span className="text-sm font-normal text-muted-foreground">{t(`landing.pricing.${plan}.period`)}</span></div>
                <CardDescription>{t(`landing.pricing.${plan}.description`)}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {(t(`landing.pricing.${plan}.features`, { returnObjects: true }) as string[]).map((feature, j) => (
                    <li key={j} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{feature}</li>
                  ))}
                </ul>
                <Link to="/auth">
                  <Button className="w-full" variant={i === 1 ? 'default' : 'outline'}>
                    {plan === 'enterprise' ? t('landing.pricing.contactSales') : t('landing.pricing.getStarted')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="container mx-auto px-4 py-20 bg-muted/30">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Trusted by operations teams</h2>
          <p className="text-muted-foreground">See what our customers are saying</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}</div>
                <p className="mb-4 text-muted-foreground">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center font-semibold text-primary-foreground">{t.avatar}</div>
                  <div><div className="font-semibold">{t.name}</div><div className="text-sm text-muted-foreground">{t.role}</div></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-20">
        <Card className="bg-primary text-primary-foreground text-center p-12">
          <h2 className="text-3xl font-bold mb-4">Ready to streamline your operations?</h2>
          <p className="mb-2">Start your free trial today. No credit card required.</p>
          <p className="mb-6 text-sm text-white/90">Works with or without AI — you're in control.</p>
          <Link to="/auth"><Button size="lg" variant="secondary">Start Free Trial</Button></Link>
        </Card>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 text-center text-muted-foreground">
        <p>© 2024 OpsGPT. All rights reserved.</p>
        <p className="text-xs mt-2">Business Operations Control System — Execution, Not Chaos</p>
      </footer>
    </div>
  );
}
